import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Zap, Activity, DollarSign, Clock, TrendingUp, Settings, Play, Pause,
  BarChart3, Cpu, ArrowRight, CheckCircle, AlertTriangle, Layers, Shield,
  RefreshCw, Atom,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, Cell, LineChart, Line, PieChart, Pie,
} from "recharts";

const PROVIDERS = ["OpenAI", "Anthropic", "Google", "Moonshot", "Mistral", "Together", "DeepSeek"];
const MODELS = ["gpt-5.2", "gpt-4o", "claude-sonnet-4", "gemini-3-flash", "kimi-k2.5", "mistral-large", "deepseek-v3"];
const DECISION_TYPES = ["model_switch", "provider_failover", "cost_optimization", "latency_optimization", "quality_upgrade"];
const API = process.env.REACT_APP_BACKEND_URL;

function generateFeedItem() {
  const now = new Date();
  const type = DECISION_TYPES[Math.floor(Math.random() * DECISION_TYPES.length)];
  const fromModel = MODELS[Math.floor(Math.random() * MODELS.length)];
  let toModel = MODELS[Math.floor(Math.random() * MODELS.length)];
  while (toModel === fromModel) toModel = MODELS[Math.floor(Math.random() * MODELS.length)];
  const savings = (Math.random() * 45 + 5).toFixed(1);
  const latencyDelta = Math.round(Math.random() * 200 - 50);
  const tokens = Math.round(Math.random() * 8000 + 200);
  return {
    id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    timestamp: now.toISOString(),
    time: now.toLocaleTimeString(),
    type,
    fromModel,
    toModel,
    savings: parseFloat(savings),
    latencyDelta,
    tokens,
    solver: Math.random() > 0.7 ? "quantum" : "classical",
    confidence: (Math.random() * 20 + 80).toFixed(1),
  };
}

function generateSavingsHistory() {
  const data = [];
  const now = Date.now();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now - i * 86400000);
    data.push({
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      savings: Math.round(Math.random() * 800 + 200),
      optimizations: Math.round(Math.random() * 500 + 100),
    });
  }
  return data;
}

function generateDecisionBreakdown() {
  return [
    { type: "Cost Optimization", count: 4218, pct: 42, color: "#10b981" },
    { type: "Latency Reduction", count: 2847, pct: 28, color: "#3b82f6" },
    { type: "Quality Upgrade", count: 1523, pct: 15, color: "#8b5cf6" },
    { type: "Provider Failover", count: 982, pct: 10, color: "#f59e0b" },
    { type: "Load Balancing", count: 512, pct: 5, color: "#ef4444" },
  ];
}

const SAVINGS_HISTORY = generateSavingsHistory();
const DECISION_BREAKDOWN = generateDecisionBreakdown();

const TYPE_CONFIG = {
  model_switch: { label: "Model Switch", color: "#3b82f6", icon: ArrowRight },
  provider_failover: { label: "Failover", color: "#f59e0b", icon: RefreshCw },
  cost_optimization: { label: "Cost Opt", color: "#10b981", icon: DollarSign },
  latency_optimization: { label: "Latency Opt", color: "#8b5cf6", icon: Clock },
  quality_upgrade: { label: "Quality Up", color: "#06b6d4", icon: TrendingUp },
};

export default function OptimizationEngine() {
  const [feed, setFeed] = useState(() => Array.from({ length: 20 }, generateFeedItem).reverse());
  const [isLive, setIsLive] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("feed");
  const feedRef = useRef(null);
  const intervalRef = useRef(null);
  const readerRef = useRef(null);

  // Rules state
  const [rules, setRules] = useState({
    maxCostPerToken: 0.003,
    maxLatency: 500,
    minQuality: 80,
    autoFailover: true,
    preferredProviders: ["OpenAI", "Anthropic", "Google"],
    quantumSolver: true,
    annealingIterations: 1000,
    costWeight: 0.4,
    latencyWeight: 0.3,
    qualityWeight: 0.3,
  });

  useEffect(() => {
    if (isLive) {
      // Connect to real SSE optimization feed
      const connectSSE = async () => {
        try {
          const response = await fetch(`${API}/api/dashboard/optimization-feed`, { credentials: "include" });
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          const read = async () => {
            while (isLive) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n");
              buffer = lines.pop() || "";

              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    setFeed((prev) => [...prev, {
                      id: data.id,
                      timestamp: data.timestamp,
                      time: new Date(data.timestamp).toLocaleTimeString(),
                      type: data.type,
                      fromModel: data.fromModel,
                      toModel: data.toModel,
                      savings: data.savings || 0,
                      latencyDelta: data.latencyDelta || 0,
                      tokens: data.tokens || 0,
                      solver: data.solver || "classical",
                      confidence: data.confidence || 90,
                    }].slice(-50));
                  } catch (e) { /* skip malformed */ }
                }
              }
            }
          };
          read().catch(() => {});
          readerRef.current = reader;
        } catch (err) {
          // Fallback to local generation if SSE fails
          intervalRef.current = setInterval(() => {
            setFeed((prev) => [...prev, generateFeedItem()].slice(-50));
          }, 2200);
        }
      };
      connectSSE();
    }
    return () => {
      clearInterval(intervalRef.current);
      if (readerRef.current) {
        try { readerRef.current.cancel(); } catch (e) {}
      }
    };
  }, [isLive]);

  useEffect(() => {
    if (feedRef.current && isLive) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [feed, isLive]);

  const totalSavings = feed.reduce((s, f) => s + f.savings, 0).toFixed(2);
  const avgConfidence = (feed.reduce((s, f) => s + parseFloat(f.confidence), 0) / feed.length).toFixed(1);
  const quantumCount = feed.filter((f) => f.solver === "quantum").length;

  const updateRule = useCallback((key, value) => {
    setRules((prev) => ({ ...prev, [key]: value }));
  }, []);

  return (
    <div className="space-y-6" data-testid="optimization-engine">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">Optimization Engine</h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">Real-time routing decisions, rules configuration, and optimization analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={isLive ? "default" : "outline"}
            size="sm"
            onClick={() => setIsLive(!isLive)}
            data-testid="live-toggle-btn"
            className={isLive ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            {isLive ? <><Pause className="w-4 h-4 mr-2" /> Live</> : <><Play className="w-4 h-4 mr-2" /> Paused</>}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Session Savings", value: `$${totalSavings}`, icon: DollarSign, color: "#10b981" },
          { label: "Decisions Made", value: feed.length, icon: Zap, color: "var(--qn-primary)" },
          { label: "Avg Confidence", value: `${avgConfidence}%`, icon: TrendingUp, color: "#8b5cf6" },
          { label: "Quantum Decisions", value: quantumCount, icon: Atom, color: "#06b6d4" },
        ].map((card) => (
          <Card key={card.label} className="bg-[var(--qn-card)] border-[var(--qn-border)]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: card.color + "15" }}>
                <card.icon className="w-5 h-5" style={{ color: card.color }} />
              </div>
              <div>
                <div className="text-xs text-[var(--qn-foreground-muted)]">{card.label}</div>
                <div className="text-xl font-bold font-outfit text-[var(--qn-foreground)]">{card.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          <TabsTrigger value="feed" data-testid="opt-tab-feed"><Activity className="w-4 h-4 mr-2" />Live Feed</TabsTrigger>
          <TabsTrigger value="rules" data-testid="opt-tab-rules"><Settings className="w-4 h-4 mr-2" />Rules</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="opt-tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
          <TabsTrigger value="dwave" data-testid="opt-tab-dwave"><Atom className="w-4 h-4 mr-2" />D-Wave</TabsTrigger>
        </TabsList>

        {/* Live Feed */}
        <TabsContent value="feed">
          <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="optimization-feed">
            <CardContent className="p-0">
              <div ref={feedRef} className="h-[480px] overflow-y-auto">
                {feed.map((item, idx) => {
                  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.model_switch;
                  const Icon = cfg.icon;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-4 px-5 py-3 border-b border-[var(--qn-border)]/30 hover:bg-[var(--qn-surface)]/30 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: cfg.color + "15" }}>
                        <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[var(--qn-foreground)]">{cfg.label}</span>
                          <Badge variant="outline" className="text-[9px]" style={{ borderColor: cfg.color + "40", color: cfg.color }}>{item.solver}</Badge>
                        </div>
                        <div className="text-[11px] text-[var(--qn-foreground-muted)] truncate">
                          <span className="font-mono">{item.fromModel}</span> <ArrowRight className="w-3 h-3 inline" /> <span className="font-mono">{item.toModel}</span>
                          <span className="ml-2 text-[var(--qn-foreground-subtle)]">{item.tokens} tokens</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono text-emerald-500">-${item.savings.toFixed(2)}</div>
                        <div className="text-[10px] text-[var(--qn-foreground-subtle)]">{item.time}</div>
                      </div>
                      <div className="text-right shrink-0 min-w-[50px]">
                        <span className={`text-[11px] font-mono ${parseFloat(item.confidence) >= 90 ? "text-emerald-500" : "text-amber-500"}`}>
                          {item.confidence}%
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Configuration */}
        <TabsContent value="rules">
          <div className="grid grid-cols-2 gap-6" data-testid="optimization-rules">
            {/* Cost Rules */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" /> Cost Controls
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Max Cost per Token</span>
                    <span className="font-mono text-[var(--qn-foreground)]">${rules.maxCostPerToken.toFixed(4)}</span>
                  </div>
                  <Slider
                    value={[rules.maxCostPerToken * 10000]}
                    onValueChange={([v]) => updateRule("maxCostPerToken", v / 10000)}
                    min={1}
                    max={100}
                    step={1}
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Cost Weight</span>
                    <span className="font-mono text-[var(--qn-foreground)]">{(rules.costWeight * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[rules.costWeight * 100]}
                    onValueChange={([v]) => updateRule("costWeight", v / 100)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Latency Rules */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" /> Latency Thresholds
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Max Latency (ms)</span>
                    <span className="font-mono text-[var(--qn-foreground)]">{rules.maxLatency}ms</span>
                  </div>
                  <Slider
                    value={[rules.maxLatency]}
                    onValueChange={([v]) => updateRule("maxLatency", v)}
                    min={50}
                    max={2000}
                    step={50}
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Latency Weight</span>
                    <span className="font-mono text-[var(--qn-foreground)]">{(rules.latencyWeight * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[rules.latencyWeight * 100]}
                    onValueChange={([v]) => updateRule("latencyWeight", v / 100)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quality Rules */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-500" /> Quality Floor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Min Quality Score</span>
                    <span className="font-mono text-[var(--qn-foreground)]">{rules.minQuality}/100</span>
                  </div>
                  <Slider
                    value={[rules.minQuality]}
                    onValueChange={([v]) => updateRule("minQuality", v)}
                    min={50}
                    max={100}
                    step={5}
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[var(--qn-foreground-muted)]">Quality Weight</span>
                    <span className="font-mono text-[var(--qn-foreground)]">{(rules.qualityWeight * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[rules.qualityWeight * 100]}
                    onValueChange={([v]) => updateRule("qualityWeight", v / 100)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Provider Preferences */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <Layers className="w-4 h-4 text-amber-500" /> Provider Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--qn-foreground-muted)]">Auto Failover</span>
                  <Switch checked={rules.autoFailover} onCheckedChange={(v) => updateRule("autoFailover", v)} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--qn-foreground-muted)]">Quantum Solver</span>
                  <Switch checked={rules.quantumSolver} onCheckedChange={(v) => updateRule("quantumSolver", v)} />
                </div>
                <div>
                  <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Preferred Providers</div>
                  <div className="flex flex-wrap gap-2">
                    {PROVIDERS.map((p) => {
                      const active = rules.preferredProviders.includes(p);
                      return (
                        <Badge
                          key={p}
                          variant={active ? "default" : "outline"}
                          className={`cursor-pointer text-[10px] ${active ? "bg-[var(--qn-primary)] text-white" : "border-[var(--qn-border)]"}`}
                          onClick={() => updateRule("preferredProviders", active ? rules.preferredProviders.filter((x) => x !== p) : [...rules.preferredProviders, p])}
                        >
                          {p}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics">
          <div className="space-y-6" data-testid="optimization-analytics">
            {/* Savings Over Time */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-outfit">Savings Over Time (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={SAVINGS_HISTORY}>
                    <defs>
                      <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                    <RechartsTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 12, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} />
                    <Area type="monotone" dataKey="savings" stroke="#10b981" fill="url(#savingsGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Decision Breakdown */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-outfit">Decision Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {DECISION_BREAKDOWN.map((row) => (
                    <div key={row.type} className="flex items-center gap-4">
                      <div className="w-32 text-xs text-[var(--qn-foreground-muted)]">{row.type}</div>
                      <div className="flex-1 h-3 rounded-full bg-[var(--qn-surface)] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${row.pct}%` }}
                          transition={{ duration: 0.6 }}
                          style={{ background: row.color }}
                        />
                      </div>
                      <div className="w-20 text-right">
                        <span className="text-xs font-mono text-[var(--qn-foreground)]">{row.count.toLocaleString()}</span>
                        <span className="text-[10px] text-[var(--qn-foreground-muted)] ml-1">({row.pct}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* D-Wave Card */}
        <TabsContent value="dwave">
          <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="dwave-engine-card">
            <CardHeader>
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <Atom className="w-5 h-5 text-cyan-500" /> D-Wave Quantum Annealing Engine
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "QPU Status", value: "Available", color: "#10b981" },
                  { label: "Annealing Speed", value: "1,000/s", color: "#06b6d4" },
                  { label: "Qubits", value: "5,760", color: "#8b5cf6" },
                ].map((stat) => (
                  <div key={stat.label} className="p-4 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] text-center">
                    <div className="text-xs text-[var(--qn-foreground-muted)] mb-1">{stat.label}</div>
                    <div className="text-lg font-bold font-mono" style={{ color: stat.color }}>{stat.value}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                  <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Solver Configuration</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Mode</span>
                      <span className="text-[var(--qn-foreground)]">Hybrid Classical + Quantum</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Framework</span>
                      <span className="text-[var(--qn-foreground)]">Morency QSI v2.1</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Annealing Iterations</span>
                      <span className="font-mono text-[var(--qn-foreground)]">{rules.annealingIterations}</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                  <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">Performance Metrics</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Quantum Advantage</span>
                      <span className="text-emerald-500 font-mono">+23.4%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Solution Quality</span>
                      <span className="font-mono text-[var(--qn-foreground)]">98.7%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[var(--qn-foreground-muted)]">Active Embeddings</span>
                      <span className="font-mono text-[var(--qn-foreground)]">4,812</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
